import React from 'react';
import { Card, Progress, Space, Typography, Tag, Button, Statistic, Row, Col } from 'antd';
import {
  WarningOutlined,
  CheckCircleOutlined,
  SettingOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { UsageData } from '../../services/usageService';

const { Text, Title } = Typography;

interface FeatureUsageCardProps {
  usage: UsageData;
  onSetLimit?: (featureCode: string) => void;
}

const FeatureUsageCard: React.FC<FeatureUsageCardProps> = ({ usage, onSetLimit }) => {
  const percentageUsed = usage.baseQuota > 0 
    ? Math.min((usage.usageCount / usage.baseQuota) * 100, 100) 
    : 0;
  
  const isOverage = usage.overageCount > 0;
  const isNearLimit = percentageUsed >= 80 && !isOverage;
  const hasHardLimit = usage.customHardLimit !== undefined && usage.customHardLimit > 0;

  const getProgressStatus = () => {
    if (isOverage) return 'exception';
    if (isNearLimit) return 'normal';
    return 'success';
  };

  const getProgressColor = () => {
    if (isOverage) return '#ff4d4f';
    if (isNearLimit) return '#faad14';
    return '#52c41a';
  };

  return (
    <Card
      hoverable
      style={{
        borderRadius: 8,
        boxShadow: isOverage ? '0 2px 8px rgba(255, 77, 79, 0.15)' : undefined,
      }}
      extra={
        onSetLimit && (
          <Button
            type="text"
            icon={<SettingOutlined />}
            onClick={() => onSetLimit(usage.featureCode)}
            size="small"
          >
            Limit Ayarla
          </Button>
        )
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <ThunderboltOutlined style={{ fontSize: 20, color: '#1890ff' }} />
            <Title level={5} style={{ margin: 0 }}>
              {usage.featureName}
            </Title>
          </Space>
          {isOverage && (
            <Tag color="warning" icon={<WarningOutlined />}>
              Limit Aşıldı
            </Tag>
          )}
          {!isOverage && percentageUsed === 0 && (
            <Tag color="default">Kullanılmadı</Tag>
          )}
        </div>

        {/* Progress Bar */}
        <div>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Kullanım: {usage.usageCount} / {usage.baseQuota}
              {usage.baseQuota > 0 && ` (${percentageUsed.toFixed(0)}%)`}
            </Text>
          </div>
          <Progress
            percent={percentageUsed}
            status={getProgressStatus()}
            strokeColor={getProgressColor()}
            showInfo={false}
          />
          {isOverage && (
            <div style={{ marginTop: 8 }}>
              <Text type="danger" style={{ fontSize: 12 }}>
                <WarningOutlined /> Aşım: +{usage.overageCount} kullanım
              </Text>
            </div>
          )}
        </div>

        {/* Statistics */}
        <Row gutter={16}>
          <Col span={12}>
            <Statistic
              title="Kalan Hak"
              value={usage.remaining}
              valueStyle={{
                color: usage.remaining === 0 ? '#ff4d4f' : '#52c41a',
                fontSize: 18,
              }}
              prefix={usage.remaining > 0 ? <CheckCircleOutlined /> : undefined}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title="Bu Ay"
              value={usage.usageCount}
              valueStyle={{ fontSize: 18 }}
            />
          </Col>
        </Row>

        {/* Pricing Info */}
        {usage.pricing && (
          <div
            style={{
              background: '#fafafa',
              padding: 12,
              borderRadius: 4,
              marginTop: 8,
            }}
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                💰 Fiyatlandırma
              </Text>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text style={{ fontSize: 12 }}>
                  Temel Fiyat: {usage.pricing.basePrice} {usage.pricing.currency}
                </Text>
              </div>
              {usage.pricing.overagePrice > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text style={{ fontSize: 12 }}>
                    Aşım: Her {usage.pricing.overageUnit} için {usage.pricing.overagePrice}{' '}
                    {usage.pricing.currency}
                  </Text>
                </div>
              )}
              {isOverage && usage.pricing.overagePrice > 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 4,
                    paddingTop: 4,
                    borderTop: '1px solid #d9d9d9',
                  }}
                >
                  <Text strong style={{ fontSize: 12 }}>
                    Tahmini Ek Ücret:
                  </Text>
                  <Text strong style={{ fontSize: 12, color: '#ff4d4f' }}>
                    ~
                    {(
                      Math.ceil(usage.overageCount / usage.pricing.overageUnit) *
                      usage.pricing.overagePrice
                    ).toFixed(2)}{' '}
                    {usage.pricing.currency}
                  </Text>
                </div>
              )}
            </Space>
          </div>
        )}

        {/* Hard Limit Info */}
        {hasHardLimit && (
          <div style={{ marginTop: 8 }}>
            <Tag color="blue" style={{ fontSize: 11 }}>
              🔒 Maksimum Limit: {usage.customHardLimit}
            </Tag>
          </div>
        )}
      </Space>
    </Card>
  );
};

export default FeatureUsageCard;

